import axios from 'axios';
import { useRef, useState, useEffect } from 'react';
import { configRefs } from '@utils/config';
import { useReRender } from './useReRender.js';

/**
 * begin playback of the specified url and return controls to render an audio
 * player interface.
 */
function useAudio(args) {
  const forceUpdate = useReRender();
  const audioRef = useRef(undefined);
  const durationMsRef = useRef(undefined);
  const [didError, setDidError] = useState(false);
  const [isLoading, setLoading] = useState(args.autoplay ?? false);
  const [isPlaying, setPlaying] = useState(args.autoplay ?? false);
  const [timeElapsedMs, setTimeElapsedMs] = useState(undefined);
  const resolvedSrcPromiseRef = useRef(undefined);
  useEffect(() => {
    if (!args.src) {
      return;
    }
    let isActiveSrc = true;
    setDidError(false);
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.playbackRate = args.playbackRate ?? 1;
    document.body.appendChild(audio);

    // cache reference to the playback element, so we can reference it later
    // on in the control methods that are exposed publicly
    audioRef.current = audio;
    resolvedSrcPromiseRef.current = new Promise(resolve => {
      getAudioUrlFromSrc(args.src).then(url => {
        if (!isActiveSrc) {
          // bail out if the active audio src has been changed since we
          // requested the absolute url
          return;
        }
        if (!url) {
          setDidError(true);
          return;
        }
        audio.src = url;
        audio.autoplay = args.autoplay ?? false;

        // begin playback instantly if autoplay is set
        if (args.autoplay) {
          startPlayback(audio).catch(err => {
            args.notifier.notify({
              id: "audio.play.error",
              body: "Der opstod en fejl ved afspilningen af lydfilen.",
              type: "error"
            });
            console.error(err);
            setDidError(true);
            args.onError?.();
          }).finally(() => {
            setLoading(false);
          });
        }

        // reset internal state so we reflect that a new file is currently
        // being played
        durationMsRef.current = undefined;
        setTimeElapsedMs(undefined);
        if (args.autoplay) {
          setPlaying(true);
        }
      }).catch(err => {
        console.warn(err);
        setDidError(true);
        args.onError?.();
      }).finally(() => resolve());
    });
    return () => {
      // register that this src is no longer actively requested
      isActiveSrc = false;

      // stop playback when the component is unmounted
      audio.pause();
      audio.remove();
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [args.src]);
  useEffect(() => {
    if (!isPlaying) {
      return;
    }
    let animationFrame;

    // subscribe to progress using an animation frame loop while the audio
    // is still playing
    const onProgress = () => {
      if (audioRef.current?.duration) {
        durationMsRef.current = audioRef.current?.duration * 1000;
      }
      if (audioRef.current?.currentTime) {
        setTimeElapsedMs(audioRef.current?.currentTime * 1000);
      }
      animationFrame = requestAnimationFrame(onProgress);
    };
    animationFrame = requestAnimationFrame(onProgress);
    return () => {
      // stop tracking progress once the component has been unmounted
      cancelAnimationFrame(animationFrame);
    };
  }, [isPlaying]);

  // bind event listeners to ensure that audio progress is going to be emitted
  // properly
  useEffect(() => {
    const audio = audioRef.current;
    const onLoaded = () => {
      if (audio) {
        durationMsRef.current = audio.duration * 1000;
        forceUpdate();
      }
    };
    const onError = () => {
      setPlaying(false);
      setTimeElapsedMs(0);
      args.onError?.();
    };
    const onEnded = () => {
      setPlaying(false);
      setTimeElapsedMs(0);
      if (audio) {
        audio.currentTime = 0;
      }
      args.onEnd?.();
    };
    const onPause = () => {
      setPlaying(false);
    };
    audio?.addEventListener("loadedmetadata", onLoaded);
    audio?.addEventListener("pause", onPause);
    audio?.addEventListener("error", onError);
    audio?.addEventListener("ended", onEnded);
    return () => {
      audio?.removeEventListener("loadedmetadata", onLoaded);
      audio?.removeEventListener("pause", onPause);
      audio?.removeEventListener("error", onError);
      audio?.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args.src, args.onEnd, forceUpdate]);
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = args.playbackRate ?? 1;
    }
  }, [args.playbackRate]);
  return {
    async resume() {
      setLoading(true);
      setPlaying(true);
      try {
        await resolvedSrcPromiseRef.current;
        await startPlayback(audioRef.current);
      } catch (err) {
        console.error(err);
        args.notifier.notify({
          id: "audio.error",
          type: "error",
          body: "Noget gik galt da vi forsøgte at afspille lyden. Prøv igen senere."
        });
        setPlaying(false);
        setTimeElapsedMs(0);
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
      } finally {
        setLoading(false);
      }
    },
    pause() {
      audioRef.current?.pause();
      setPlaying(false);
      setLoading(false);
    },
    seek(timeMs) {
      if (audioRef.current) {
        audioRef.current.currentTime = timeMs / 1000;
        setTimeElapsedMs(timeMs);
      }
    },
    didError,
    isLoading,
    isPlaying,
    durationMs: durationMsRef.current,
    timeElapsedMs: timeElapsedMs
  };
}
async function startPlayback(audioElement) {
  if (!audioElement?.src) {
    throw new Error("useAudio.resume(): Failed to play audio, src was not resolved properly");
  }
  await audioElement.play();

  // Stop playback from all other possibly active media to avoid
  // multiple sources playing at the same time.
  for (const mediaPlayer of Array.from(document.body.querySelectorAll("audio, video"))) {
    if (mediaPlayer !== audioElement && !mediaPlayer.paused) {
      mediaPlayer.pause();
    }
  }
}
const urlCache = new Map();

/**
 * resolve absolute url for media files stored in Gyldendals MediaLibrary
 */
async function getAudioUrlFromSrc(src) {
  if (!src) {
    return undefined;
  }
  const cachedUrl = urlCache.get(src);
  if (cachedUrl) {
    return cachedUrl;
  }
  if (src.includes("://")) {
    // return as is if we were already given an absolute URL
    return src;
  }
  const response = await axios.get(`${await configRefs.apis.mediaLibrary.get()}/api/mediafiles/${src}`);
  if (!response.data) {
    return;
  }
  if (Array.isArray(response.data)) {
    const url = response.data.map(getMp3Url).find(it => it !== undefined);
    if (url) {
      urlCache.set(src, url);
    }
    return url;
  } else {
    const url = getMp3Url(response.data);
    if (url) {
      urlCache.set(src, url);
    }
    return url;
  }
}
function getMp3Url(item) {
  return item.Mp3Url;
}

export { getAudioUrlFromSrc, useAudio };
//# sourceMappingURL=audio.js.map
